import React, { useEffect, useState } from "react";
import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";
import { useLocation, useParams } from "react-router-dom";

import { useDecodedAuthPrincipal } from "AuthPrincipal";

const ddApplicationID = process.env.REACT_APP_DD_RUM_APPLICATION_ID;
const ddClientToken = process.env.REACT_APP_DD_RUM_CLIENT_TOKEN;
const ddEnvironment =
  process.env.REACT_APP_DD_RUM_ENVIRONMENT ??
  process.env.REACT_APP_MONITORING_ENV ??
  process.env.NODE_ENV;

export const useDatadog = () => {
  const [ddInit, setDdInit] = useState(false);
  const principal = useDecodedAuthPrincipal();
  useEffect(() => {
    if (!ddInit && ddEnvironment && ddApplicationID && ddClientToken) {
      const ddConfig = {
        applicationId: ddApplicationID,
        clientToken: ddClientToken,
        env: ddEnvironment,
        service: "assortments-ui",
        silentMultipleInit: true,
        trackSessionAcrossSubdomains: true,
      };
      datadogRum.init({
        ...ddConfig,
        allowedTracingOrigins:
          process.env.REACT_APP_GRAPHQL_URL !== undefined
            ? [new URL(process.env.REACT_APP_GRAPHQL_URL).origin]
            : undefined,
        beforeSend: (event, context) => {
          event.context = {
            ...event.context,
            response:
              "response" in context && context.response
                ? {
                    headers: context.response.headers,
                    body: context.response.body,
                    status: context.response.status,
                    url: context.response.url,
                  }
                : undefined,
            request:
              "requestInit" in context && context.requestInit
                ? {
                    method: context.requestInit.method,
                    body:
                      typeof context.requestInit.body === "string"
                        ? JSON.parse(context.requestInit.body)
                        : context.requestInit.body,
                  }
                : undefined,
            error: "error" in context && context.error,
          };
        },
      });
      datadogLogs.init({
        ...ddConfig,
        forwardErrorsToLogs: true,
      });
      if (process.env.NODE_ENV !== "development") {
        logger.setHandler("http");
      }
      setDdInit(true);
    }
  }, [ddInit, setDdInit]);

  useEffect(() => {
    if (ddInit && principal) {
      datadogRum.addRumGlobalContext("user", principal.user);
      datadogRum.addRumGlobalContext("account", principal.account);
      datadogRum.setUser({
        id: principal.user.id,
        name: principal.user.displayName ?? undefined,
        email: principal.user.email ?? undefined,
      });
    }
  }, [ddInit, principal]);
};

export const logger = datadogLogs.createLogger("ddLogger", {
  handler: "console",
});

export type ErrorReport = {
  message: string;
  context?: any;
  errors?: (Error | ErrorReport)[];
};
export const reportError = ({ message, ...report }: ErrorReport) => {
  logger.error(message, report);
  datadogRum.addError({ message, ...report }, report);
};

export const DDRoute: React.FC<{ view: string }> = ({ children, view }) => {
  useEffect(() => {
    datadogRum.startView(view);
  }, [view]);
  const location = useLocation();
  useEffect(() => {
    datadogRum.addRumGlobalContext("location", location);
  }, [location]);
  const params = useParams();
  useEffect(() => {
    datadogRum.addRumGlobalContext("workspaceId", params.id);
  }, [params]);
  return <>{children}</>;
};
